"use client"

import { useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { usePostHog } from 'posthog-js/react'
import { SiteHeader } from "@/components/site-header"
import { SiteFooter } from "@/components/site-footer"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { 
  MapPin, 
  Zap, 
  Search,
  TrendingUp,
  ChevronRight,
  Star,
  DollarSign,
  Leaf,
  Snowflake,
  Loader2,
  Globe,
  BarChart3
} from "lucide-react"
import {
  FIS_CANONICAL_DEFINITION,
  FIS_LOCATION_FACTORS,
  FIS_SCOPE_SUMMARY,
} from "@/lib/fis-factors"

/** One icon per entry in FIS_LOCATION_FACTORS, in the same order. */
const FIS_FACTOR_ICONS = [Zap, Snowflake, DollarSign, Star] as const
import { LocalShareButtons } from "@/components/blocks/local-share-buttons"
import { BestsellersCarousel } from "@/components/blocks/bestsellers-carousel"
import { LocationStatCard } from "@/components/brand-kit/location-stat-card"
import { FAQAccordion } from "@/components/brand-kit/faq-accordion"
import { Breadcrumb } from "@/components/brand-kit/breadcrumb"
import { localBreadcrumbs } from "@/lib/breadcrumbs"
import { createClient } from "@/lib/supabase/client"
import { STATE_SLUGS } from "@/lib/state-utils"
import { buildLocalIndexFaqs } from "@/lib/local/local-index-faqs"

import type { LocalHubData } from '@/lib/local/local-energy-data'

// Featured collections for different needs
const FEATURED_COLLECTIONS = [
  { slug: 'energy-star-refrigerators', name: 'Energy Star Refrigerators', icon: Leaf, desc: 'Compare energy-efficient models' },
  { slug: 'garage-refrigerators', name: 'Garage Refrigerators', icon: Snowflake, desc: 'Built for extreme temperatures' },
  { slug: 'chest-freezers', name: 'Chest Freezers', icon: Snowflake, desc: 'Maximum storage efficiency' },
  { slug: 'french-door-refrigerators', name: 'French Door Refrigerators', icon: Star, desc: 'Most popular style' },
]

// Simple zip to state mapping (first 3 digits)
const ZIP_TO_STATE: Record<string, string> = {
  '005': 'NY', '006': 'PR', '007': 'PR', '008': 'PR', '009': 'PR',
  '010': 'MA', '011': 'MA', '012': 'MA', '013': 'MA', '014': 'MA', '015': 'MA', '016': 'MA', '017': 'MA', '018': 'MA', '019': 'MA',
  '020': 'MA', '021': 'MA', '022': 'MA', '023': 'MA', '024': 'MA', '025': 'RI', '026': 'RI', '027': 'RI', '028': 'RI', '029': 'RI',
  '030': 'NH', '031': 'NH', '032': 'NH', '033': 'NH', '034': 'NH', '035': 'VT', '036': 'VT', '037': 'VT', '038': 'NH', '039': 'ME',
  '040': 'ME', '041': 'ME', '042': 'ME', '043': 'ME', '044': 'ME', '045': 'ME', '046': 'ME', '047': 'ME', '048': 'ME', '049': 'ME',
  '050': 'VT', '051': 'VT', '052': 'VT', '053': 'VT', '054': 'VT', '055': 'MA', '056': 'VT', '057': 'VT', '058': 'VT', '059': 'VT',
  '060': 'CT', '061': 'CT', '062': 'CT', '063': 'CT', '064': 'CT', '065': 'CT', '066': 'CT', '067': 'CT', '068': 'CT', '069': 'CT',
  '070': 'NJ', '071': 'NJ', '072': 'NJ', '073': 'NJ', '074': 'NJ', '075': 'NJ', '076': 'NJ', '077': 'NJ', '078': 'NJ', '079': 'NJ',
  '080': 'NJ', '081': 'NJ', '082': 'NJ', '083': 'NJ', '084': 'NJ', '085': 'NJ', '086': 'NJ', '087': 'NJ', '088': 'NJ', '089': 'NJ',
  '100': 'NY', '101': 'NY', '102': 'NY', '103': 'NY', '104': 'NY', '105': 'NY', '106': 'NY', '107': 'NY', '108': 'NY', '109': 'NY',
  '110': 'NY', '111': 'NY', '112': 'NY', '113': 'NY', '114': 'NY', '115': 'NY', '116': 'NY', '117': 'NY', '118': 'NY', '119': 'NY',
  '120': 'NY', '121': 'NY', '122': 'NY', '123': 'NY', '124': 'NY', '125': 'NY', '126': 'NY', '127': 'NY', '128': 'NY', '129': 'NY',
  '130': 'NY', '131': 'NY', '132': 'NY', '133': 'NY', '134': 'NY', '135': 'NY', '136': 'NY', '137': 'NY', '138': 'NY', '139': 'NY',
  '140': 'NY', '141': 'NY', '142': 'NY', '143': 'NY', '144': 'NY', '145': 'NY', '146': 'NY', '147': 'NY', '148': 'NY', '149': 'NY',
  '150': 'PA', '151': 'PA', '152': 'PA', '153': 'PA', '154': 'PA', '155': 'PA', '156': 'PA', '157': 'PA', '158': 'PA', '159': 'PA',
  '160': 'PA', '161': 'PA', '162': 'PA', '163': 'PA', '164': 'PA', '165': 'PA', '166': 'PA', '167': 'PA', '168': 'PA', '169': 'PA',
  '170': 'PA', '171': 'PA', '172': 'PA', '173': 'PA', '174': 'PA', '175': 'PA', '176': 'PA', '177': 'PA', '178': 'PA', '179': 'PA',
  '180': 'PA', '181': 'PA', '182': 'PA', '183': 'PA', '184': 'PA', '185': 'PA', '186': 'PA', '187': 'PA', '188': 'PA', '189': 'PA',
  '190': 'PA', '191': 'PA', '192': 'PA', '193': 'PA', '194': 'PA', '195': 'DE', '196': 'DE', '197': 'DE', '198': 'DE', '199': 'DE',
  '200': 'DC', '201': 'VA', '202': 'DC', '203': 'DC', '204': 'DC', '205': 'DC', '206': 'MD', '207': 'MD', '208': 'MD', '209': 'MD',
  '210': 'MD', '211': 'MD', '212': 'MD', '214': 'MD', '215': 'MD', '216': 'MD', '217': 'MD', '218': 'MD', '219': 'MD',
  '220': 'VA', '221': 'VA', '222': 'VA', '223': 'VA', '224': 'VA', '225': 'VA', '226': 'VA', '227': 'VA', '228': 'VA', '229': 'VA',
  '230': 'VA', '231': 'VA', '232': 'VA', '233': 'VA', '234': 'VA', '235': 'VA', '236': 'VA', '237': 'VA', '238': 'VA', '239': 'VA',
  '240': 'VA', '241': 'VA', '242': 'VA', '243': 'VA', '244': 'VA', '245': 'VA', '246': 'VA',
  '247': 'WV', '248': 'WV', '249': 'WV', '250': 'WV', '251': 'WV', '252': 'WV', '253': 'WV', '254': 'WV', '255': 'WV', '256': 'WV',
  '257': 'WV', '258': 'WV', '259': 'WV', '260': 'WV', '261': 'WV', '262': 'WV', '263': 'WV', '264': 'WV', '265': 'WV', '266': 'WV',
  '267': 'WV', '268': 'WV',
  '270': 'NC', '271': 'NC', '272': 'NC', '273': 'NC', '274': 'NC', '275': 'NC', '276': 'NC', '277': 'NC', '278': 'NC', '279': 'NC',
  '280': 'NC', '281': 'NC', '282': 'NC', '283': 'NC', '284': 'NC', '285': 'NC', '286': 'NC', '287': 'NC', '288': 'NC', '289': 'NC',
  '290': 'SC', '291': 'SC', '292': 'SC', '293': 'SC', '294': 'SC', '295': 'SC', '296': 'SC', '297': 'SC', '298': 'SC', '299': 'SC',
  '300': 'GA', '301': 'GA', '302': 'GA', '303': 'GA', '304': 'GA', '305': 'GA', '306': 'GA', '307': 'GA', '308': 'GA', '309': 'GA',
  '310': 'GA', '311': 'GA', '312': 'GA', '313': 'GA', '314': 'GA', '315': 'GA', '316': 'GA', '317': 'GA', '318': 'GA', '319': 'GA',
  '320': 'FL', '321': 'FL', '322': 'FL', '323': 'FL', '324': 'FL', '325': 'FL', '326': 'FL', '327': 'FL', '328': 'FL', '329': 'FL',
  '330': 'FL', '331': 'FL', '332': 'FL', '333': 'FL', '334': 'FL', '335': 'FL', '336': 'FL', '337': 'FL', '338': 'FL', '339': 'FL',
  '340': 'FL', '341': 'FL', '342': 'FL', '344': 'FL', '346': 'FL', '347': 'FL', '349': 'FL',
  '350': 'AL', '351': 'AL', '352': 'AL', '354': 'AL', '355': 'AL', '356': 'AL', '357': 'AL', '358': 'AL', '359': 'AL',
  '360': 'AL', '361': 'AL', '362': 'AL', '363': 'AL', '364': 'AL', '365': 'AL', '366': 'AL', '367': 'AL', '368': 'AL', '369': 'AL',
  '370': 'TN', '371': 'TN', '372': 'TN', '373': 'TN', '374': 'TN', '375': 'TN', '376': 'TN', '377': 'TN', '378': 'TN', '379': 'TN',
  '380': 'TN', '381': 'TN', '382': 'TN', '383': 'TN', '384': 'TN', '385': 'TN',
  '386': 'MS', '387': 'MS', '388': 'MS', '389': 'MS', '390': 'MS', '391': 'MS', '392': 'MS', '393': 'MS', '394': 'MS', '395': 'MS',
  '396': 'MS', '397': 'MS',
  '400': 'KY', '401': 'KY', '402': 'KY', '403': 'KY', '404': 'KY', '405': 'KY', '406': 'KY', '407': 'KY', '408': 'KY', '409': 'KY',
  '410': 'KY', '411': 'KY', '412': 'KY', '413': 'KY', '414': 'KY', '415': 'KY', '416': 'KY', '417': 'KY', '418': 'KY',
  '420': 'KY', '421': 'KY', '422': 'KY', '423': 'KY', '424': 'KY', '425': 'KY', '426': 'KY', '427': 'KY',
  '430': 'OH', '431': 'OH', '432': 'OH', '433': 'OH', '434': 'OH', '435': 'OH', '436': 'OH', '437': 'OH', '438': 'OH', '439': 'OH',
  '440': 'OH', '441': 'OH', '442': 'OH', '443': 'OH', '444': 'OH', '445': 'OH', '446': 'OH', '447': 'OH', '448': 'OH', '449': 'OH',
  '450': 'OH', '451': 'OH', '452': 'OH', '453': 'OH', '454': 'OH', '455': 'OH', '456': 'OH', '457': 'OH', '458': 'OH', '459': 'OH',
  '460': 'IN', '461': 'IN', '462': 'IN', '463': 'IN', '464': 'IN', '465': 'IN', '466': 'IN', '467': 'IN', '468': 'IN', '469': 'IN',
  '470': 'IN', '471': 'IN', '472': 'IN', '473': 'IN', '474': 'IN', '475': 'IN', '476': 'IN', '477': 'IN', '478': 'IN', '479': 'IN',
  '480': 'MI', '481': 'MI', '482': 'MI', '483': 'MI', '484': 'MI', '485': 'MI', '486': 'MI', '487': 'MI', '488': 'MI', '489': 'MI',
  '490': 'MI', '491': 'MI', '492': 'MI', '493': 'MI', '494': 'MI', '495': 'MI', '496': 'MI', '497': 'MI', '498': 'MI', '499': 'MI',
  '500': 'IA', '501': 'IA', '502': 'IA', '503': 'IA', '504': 'IA', '505': 'IA', '506': 'IA', '507': 'IA', '508': 'IA', '509': 'IA',
  '510': 'IA', '511': 'IA', '512': 'IA', '513': 'IA', '514': 'IA', '515': 'IA', '516': 'IA',
  '520': 'IA', '521': 'IA', '522': 'IA', '523': 'IA', '524': 'IA', '525': 'IA', '526': 'IA', '527': 'IA', '528': 'IA',
  '530': 'WI', '531': 'WI', '532': 'WI', '534': 'WI', '535': 'WI', '537': 'WI', '538': 'WI', '539': 'WI',
  '540': 'WI', '541': 'WI', '542': 'WI', '543': 'WI', '544': 'WI', '545': 'WI', '546': 'WI', '547': 'WI', '548': 'WI', '549': 'WI',
  '550': 'MN', '551': 'MN', '553': 'MN', '554': 'MN', '555': 'MN', '556': 'MN', '557': 'MN', '558': 'MN', '559': 'MN',
  '560': 'MN', '561': 'MN', '562': 'MN', '563': 'MN', '564': 'MN', '565': 'MN', '566': 'MN', '567': 'MN',
  '570': 'SD', '571': 'SD', '572': 'SD', '573': 'SD', '574': 'SD', '575': 'SD', '576': 'SD', '577': 'SD',
  '580': 'ND', '581': 'ND', '582': 'ND', '583': 'ND', '584': 'ND', '585': 'ND', '586': 'ND', '587': 'ND', '588': 'ND',
  '590': 'MT', '591': 'MT', '592': 'MT', '593': 'MT', '594': 'MT', '595': 'MT', '596': 'MT', '597': 'MT', '598': 'MT', '599': 'MT',
  '600': 'IL', '601': 'IL', '602': 'IL', '603': 'IL', '604': 'IL', '605': 'IL', '606': 'IL', '607': 'IL', '608': 'IL', '609': 'IL',
  '610': 'IL', '611': 'IL', '612': 'IL', '613': 'IL', '614': 'IL', '615': 'IL', '616': 'IL', '617': 'IL', '618': 'IL', '619': 'IL',
  '620': 'IL', '621': 'IL', '622': 'IL', '623': 'IL', '624': 'IL', '625': 'IL', '626': 'IL', '627': 'IL', '628': 'IL', '629': 'IL',
  '630': 'MO', '631': 'MO', '633': 'MO', '634': 'MO', '635': 'MO', '636': 'MO', '637': 'MO', '638': 'MO', '639': 'MO',
  '640': 'MO', '641': 'MO', '644': 'MO', '645': 'MO', '646': 'MO', '647': 'MO', '648': 'MO', '649': 'MO',
  '650': 'MO', '651': 'MO', '652': 'MO', '653': 'MO', '654': 'MO', '655': 'MO', '656': 'MO', '657': 'MO', '658': 'MO',
  '660': 'KS', '661': 'KS', '662': 'KS', '664': 'KS', '665': 'KS', '666': 'KS', '667': 'KS', '668': 'KS', '669': 'KS',
  '670': 'KS', '671': 'KS', '672': 'KS', '673': 'KS', '674': 'KS', '675': 'KS', '676': 'KS', '677': 'KS', '678': 'KS', '679': 'KS',
  '680': 'NE', '681': 'NE', '683': 'NE', '684': 'NE', '685': 'NE', '686': 'NE', '687': 'NE', '688': 'NE', '689': 'NE',
  '690': 'NE', '691': 'NE', '692': 'NE', '693': 'NE',
  '700': 'LA', '701': 'LA', '703': 'LA', '704': 'LA', '705': 'LA', '706': 'LA', '707': 'LA', '708': 'LA',
  '710': 'LA', '711': 'LA', '712': 'LA', '713': 'LA', '714': 'LA',
  '716': 'AR', '717': 'AR', '718': 'AR', '719': 'AR', '720': 'AR', '721': 'AR', '722': 'AR', '723': 'AR', '724': 'AR', '725': 'AR',
  '726': 'AR', '727': 'AR', '728': 'AR', '729': 'AR',
  '730': 'OK', '731': 'OK', '734': 'OK', '735': 'OK', '736': 'OK', '737': 'OK', '738': 'OK', '739': 'OK',
  '740': 'OK', '741': 'OK', '743': 'OK', '744': 'OK', '745': 'OK', '746': 'OK', '747': 'OK', '748': 'OK', '749': 'OK',
  '750': 'TX', '751': 'TX', '752': 'TX', '753': 'TX', '754': 'TX', '755': 'TX', '756': 'TX', '757': 'TX', '758': 'TX', '759': 'TX',
  '760': 'TX', '761': 'TX', '762': 'TX', '763': 'TX', '764': 'TX', '765': 'TX', '766': 'TX', '767': 'TX', '768': 'TX', '769': 'TX',
  '770': 'TX', '771': 'TX', '772': 'TX', '773': 'TX', '774': 'TX', '775': 'TX', '776': 'TX', '777': 'TX', '778': 'TX', '779': 'TX',
  '780': 'TX', '781': 'TX', '782': 'TX', '783': 'TX', '784': 'TX', '785': 'TX', '786': 'TX', '787': 'TX', '788': 'TX', '789': 'TX',
  '790': 'TX', '791': 'TX', '792': 'TX', '793': 'TX', '794': 'TX', '795': 'TX', '796': 'TX', '797': 'TX', '798': 'TX', '799': 'TX',
  '800': 'CO', '801': 'CO', '802': 'CO', '803': 'CO', '804': 'CO', '805': 'CO', '806': 'CO', '807': 'CO', '808': 'CO', '809': 'CO',
  '810': 'CO', '811': 'CO', '812': 'CO', '813': 'CO', '814': 'CO', '815': 'CO', '816': 'CO',
  '820': 'WY', '821': 'WY', '822': 'WY', '823': 'WY', '824': 'WY', '825': 'WY', '826': 'WY', '827': 'WY', '828': 'WY', '829': 'WY',
  '830': 'WY', '831': 'WY',
  '832': 'ID', '833': 'ID', '834': 'ID', '835': 'ID', '836': 'ID', '837': 'ID', '838': 'ID',
  '840': 'UT', '841': 'UT', '842': 'UT', '843': 'UT', '844': 'UT', '845': 'UT', '846': 'UT', '847': 'UT',
  '850': 'AZ', '851': 'AZ', '852': 'AZ', '853': 'AZ', '855': 'AZ', '856': 'AZ', '857': 'AZ', '859': 'AZ',
  '860': 'AZ', '863': 'AZ', '864': 'AZ', '865': 'AZ',
  '870': 'NM', '871': 'NM', '872': 'NM', '873': 'NM', '874': 'NM', '875': 'NM', '877': 'NM', '878': 'NM', '879': 'NM',
  '880': 'NM', '881': 'NM', '882': 'NM', '883': 'NM', '884': 'NM',
  '889': 'NV', '890': 'NV', '891': 'NV', '893': 'NV', '894': 'NV', '895': 'NV', '897': 'NV', '898': 'NV',
  '900': 'CA', '901': 'CA', '902': 'CA', '903': 'CA', '904': 'CA', '905': 'CA', '906': 'CA', '907': 'CA', '908': 'CA',
  '910': 'CA', '911': 'CA', '912': 'CA', '913': 'CA', '914': 'CA', '915': 'CA', '916': 'CA', '917': 'CA', '918': 'CA',
  '920': 'CA', '921': 'CA', '922': 'CA', '923': 'CA', '924': 'CA', '925': 'CA', '926': 'CA', '927': 'CA', '928': 'CA',
  '930': 'CA', '931': 'CA', '932': 'CA', '933': 'CA', '934': 'CA', '935': 'CA', '936': 'CA', '937': 'CA', '938': 'CA', '939': 'CA',
  '940': 'CA', '941': 'CA', '942': 'CA', '943': 'CA', '944': 'CA', '945': 'CA', '946': 'CA', '947': 'CA', '948': 'CA', '949': 'CA',
  '950': 'CA', '951': 'CA', '952': 'CA', '953': 'CA', '954': 'CA', '955': 'CA', '956': 'CA', '957': 'CA', '958': 'CA', '959': 'CA',
  '960': 'CA', '961': 'CA',
  '967': 'HI', '968': 'HI',
  '970': 'OR', '971': 'OR', '972': 'OR', '973': 'OR', '974': 'OR', '975': 'OR', '976': 'OR', '977': 'OR', '978': 'OR', '979': 'OR',
  '980': 'WA', '981': 'WA', '982': 'WA', '983': 'WA', '984': 'WA', '985': 'WA', '986': 'WA', '988': 'WA', '989': 'WA',
  '990': 'WA', '991': 'WA', '992': 'WA', '993': 'WA', '994': 'WA',
  '995': 'AK', '996': 'AK', '997': 'AK', '998': 'AK', '999': 'AK',
}

// ZIP preview data interface
interface ZipPreviewData {
  zip: string
  city_slug: string
  city_name: string
  state_code: string
  rate: number
  utility_name: string | null
}

export function LocalHubClient({ data }: { data: LocalHubData | null }) {
  const states = data?.states ?? []
  const featuredCities = data?.featuredCities ?? []
  const faqs = buildLocalIndexFaqs(states)
  const router = useRouter()
  const posthog = usePostHog()
  const [zipCode, setZipCode] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [previewData, setPreviewData] = useState<ZipPreviewData | null>(null)


  const handleZipLookup = async () => {
    setError('')
    setPreviewData(null)
    setIsLoading(true)
    
    // Validate zip code
    const cleanZip = zipCode.replace(/\D/g, '')
    if (cleanZip.length !== 5) {
      setError('Please enter a valid 5-digit zip code')
      setIsLoading(false)
      return
    }

    try {
      // Query zip_energy_data for the exact ZIP code
      const supabase = createClient()
      const { data: zipData, error: dbError } = await supabase
        .from('zip_energy_data')
        .select('zip, city_slug, city_name, state_code, res_rate_kwh, utility_name')
        .eq('zip', cleanZip)
        .single()

      if (dbError || !zipData) {
        // ZIP not in database - fall back to state-level navigation
        const prefix = cleanZip.substring(0, 3)
        const stateCode = ZIP_TO_STATE[prefix]
        
        if (!stateCode || !STATE_SLUGS[stateCode]) {
          setError('Could not find data for this zip code')
          setIsLoading(false)
          return
        }
        
        // Navigate to state page as fallback
        router.push(`/local/${STATE_SLUGS[stateCode]}`)
        return
      }

      const rate = Number(zipData.res_rate_kwh) * 100
      if (!Number.isFinite(rate) || rate <= 0 || !STATE_SLUGS[zipData.state_code] || !zipData.city_slug) {
        setError('Electricity rate data is unavailable for this ZIP code')
        setIsLoading(false)
        return
      }

      // Track ZIP lookup (preview shown)
      if (posthog) {
        posthog.capture('zip_lookup', {
          source_page: 'local_index',
          searched_zip: cleanZip,
          found_city: zipData.city_slug,
          found_state: zipData.state_code,
          preview_shown: true
        })
      }

      // Show preview instead of navigating
      setPreviewData({ ...zipData, rate } as ZipPreviewData)
      setIsLoading(false)
    } catch {
      // On error, try state-level fallback
      const prefix = cleanZip.substring(0, 3)
      const stateCode = ZIP_TO_STATE[prefix]
      
      if (stateCode && STATE_SLUGS[stateCode]) {
        router.push(`/local/${STATE_SLUGS[stateCode]}`)
      } else {
        setError('Could not find data for this zip code')
        setIsLoading(false)
      }
    }
  }

  const handleViewDetails = () => {
    if (!previewData) return
    
    // Track click-through from preview
    if (posthog) {
      posthog.capture('zip_preview_click', {
        source_page: 'local_index',
        zip: previewData.zip,
        city: previewData.city_slug
      })
    }
  }

  const clearPreview = () => {
    setPreviewData(null)
    setZipCode('')
  }

  // Sort states by rate for display
  const highestRateStates = [...states].sort((a, b) => b.rate - a.rate).slice(0, 5)
  const lowestRateStates = [...states].sort((a, b) => a.rate - b.rate).slice(0, 5)

  return (
    <div className="ed-root min-h-screen bg-white">
      <SiteHeader />

      <main id="main-content">
        {/* HERO — editorial chrome */}
        <section style={{ borderBottom: '1px solid #f3f4f6' }}>
          <div className="ed-container" style={{ paddingTop: 36, paddingBottom: 36 }}>
            {/* ONE trail (lib/breadcrumbs.ts) — LocalIndexSchemas emits the same
                localBreadcrumbs() as the BreadcrumbList (FRIDGE-269). */}
            <Breadcrumb variant="editorial" trail={localBreadcrumbs()} style={{ marginBottom: 24 }} />

            <div className="ed-eyebrow" style={{ marginBottom: 18 }}>
              <span className="dot" />
              LOCAL FRIDGE FINDER · STATE AND ZIP ELECTRICITY DATA
            </div>

            <h1 className="ed-h1" style={{ fontSize: 'clamp(40px, 5vw, 72px)' }}>
              Best refrigerators by{' '}
              <em style={{ fontStyle: 'italic', color: 'var(--fridge-orange)' }}>state &amp; city</em>
            </h1>
            <p className="ed-subtitle" style={{ maxWidth: 720, fontSize: 17, marginTop: 12 }}>
              Powered by the Fridge.com Intelligence Score (FIS) — explore
              how electricity costs and climate affect appliance choices in your area.
            </p>

            <div style={{ marginTop: 18 }}>
              <LocalShareButtons locationName="Local Fridge Finder" variant="top" />
            </div>
          </div>
        </section>

        {/* STATS ROW — editorial 4-cell grid (matches /local/[state] + /rebates/[state]) */}
        <section style={{ borderBottom: '1px solid #f3f4f6' }}>
          <div className="ed-container" style={{ paddingTop: 28, paddingBottom: 28 }}>
            <div className="ed-local-stats">
              <div className="cell tint">
                <div className="k">STATES &amp; DC</div>
                <div className="v">{data ? states.length.toLocaleString() : 'Unavailable'}</div>
                <div className="l">Database coverage for state residential average rates.</div>
                <div className="src-credit">Source details accompany the state rates below.</div>
              </div>
              <div className="cell">
                <div className="k">ZIP CODES</div>
                <div className="v">{data ? data.zipCount.toLocaleString() : 'Unavailable'}</div>
                <div className="l">Utility-average rate estimates associated with ZIP codes.</div>
              </div>
              <div className="cell">
                <div className="k">CITIES</div>
                <div className="v">{data ? data.supportedCityCount.toLocaleString() : 'Unavailable'}</div>
                <div className="l">Cities with input-supported historical FIS scores.</div>
              </div>
              <div className="cell">
                <div className="k">DATA SOURCE</div>
                <div className="v">By location</div>
                <div className="l">State averages and ZIP estimates have different source periods.</div>
                <div className="src-credit">Source details accompany the state rates below.</div>
              </div>
            </div>
          </div>
        </section>

        {/* ZIP Code Lookup */}
        <section style={{ borderBottom: '1px solid #f3f4f6' }}>
          <div className="ed-container" style={{ paddingTop: 40, paddingBottom: 40 }}>
            <div className="ed-grid-2col">
              <div className="min-w-0">
                <div className="ed-eyebrow" style={{ marginBottom: 12 }}>
                  <span className="dot" />
                  YOUR LOCAL RATE
                </div>
                <h2
                  style={{
                    fontSize: 30,
                    fontWeight: 900,
                    letterSpacing: '-0.025em',
                    margin: '0 0 12px',
                    textWrap: 'balance' as never,
                  }}
                >
                  Find rates and savings for{' '}
                  <em style={{ fontStyle: 'italic', color: 'var(--fridge-orange)' }}>your</em> ZIP.
                </h2>
                <p style={{ fontSize: 15, color: 'var(--gray-600)', lineHeight: 1.55, margin: 0, maxWidth: 460 }}>
                  Enter your ZIP — we look up an associated utility, an estimated rate, and a historical FIS score,
                  then surface fridge recommendations tuned to your local conditions.
                </p>
              </div>
              <div className="min-w-0 bg-gray-50 rounded-xl p-5 sm:p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#0469f0]/10 flex items-center justify-center">
                  <Search className="w-5 h-5 text-[#0469f0]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Enter your ZIP code</h3>
              </div>
              <div className="flex gap-2">
                <Input
                  type="text"
                  inputMode="numeric"
                  aria-label="ZIP code"
                  placeholder="e.g., 90210"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleZipLookup()}
                  className="min-w-0 flex-1 text-lg"
                  maxLength={5}
                />
                <button
                  onClick={handleZipLookup}
                  disabled={isLoading}
                  className="shrink-0 inline-flex items-center gap-2 bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] font-semibold text-sm px-5 py-2.5 rounded-md transition-colors disabled:opacity-100 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <MapPin className="h-4 w-4" />
                  )}
                  {isLoading ? 'Finding...' : 'Find'}
                </button>
              </div>
              {error && (
                <p className="text-red-600 text-sm mt-2">{error}</p>
              )}

              {previewData && (
                <div className="mt-4 p-4 bg-white rounded-xl border border-gray-200">
                  <p className="text-lg font-semibold text-gray-900">
                    {previewData.city_name}, {previewData.state_code} {previewData.zip}
                  </p>
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Estimated utility-average rate</p>
                      <p className="text-xl font-bold text-[#0469f0]">{previewData.rate.toFixed(1)}¢/kWh</p>
                    </div>
                    {previewData.utility_name && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Associated utility</p>
                        <p className="text-sm font-medium text-gray-900">{previewData.utility_name}</p>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mt-3">Your tariff may differ from this estimate. Open full details for the data source.</p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <Link
                      href={`/local/${STATE_SLUGS[previewData.state_code]}/${previewData.city_slug}/${previewData.zip}`}
                      onClick={handleViewDetails}
                      className="flex-1"
                    >
                      <Button variant="ctaPrimary" className="w-full font-semibold">
                        View Full Details
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </Link>
                    <Button variant="outline" size="default" onClick={clearPreview}>
                      Search Another
                    </Button>
                  </div>
                </div>
              )}

              {!previewData && (
                <p className="text-sm text-gray-600 mt-3">
                  Get personalized refrigerator &amp; freezer recommendations based on your estimated electricity rates and climate zone.
                </p>
              )}
              </div>
            </div>
          </div>
        </section>

        {/* Highest supported historical city scores, with deterministic ties. */}
        {featuredCities.length > 0 && <section style={{ borderBottom: '1px solid #f3f4f6', background: 'var(--gray-50)' }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>Highest city FIS urgency scores</h2>
              <span className="meta">historical method · higher means greater upgrade urgency</span>
            </div>
            <p className="text-sm text-gray-600 mb-4">These stored scores reproduce the historical formula from saved inputs. Current household rebate eligibility is not certified. <Link href="/about-us/fis" className="underline">Read the FIS methodology.</Link></p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {featuredCities.map((city) => (
                <Link
                  key={`${city.name}-${city.state}`}
                  href={`/local/${STATE_SLUGS[city.state]}/${city.slug}`}
                >
                  <div className="bg-white border border-gray-200 rounded-xl p-3 hover:shadow-md hover:border-[#ff9951] transition-all h-full">
                    <div className="flex items-start gap-2">
                      <Image
                        src={`/state-flags/fridge.com-${city.state.toLowerCase()}-flag.png`}
                        alt={`Flag of ${city.state} - Fridge.com`}
                        width={48}
                        height={30}
                        className="rounded border border-gray-200 shadow-sm flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-gray-900 truncate">{city.name}</p>
                        <p className="text-xs text-gray-500">{city.state}</p>
                      </div>
                      <span className="bg-[#ff9951] text-[var(--color-brand-action-text)] text-[10px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0">
                        {city.fis}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>}

        {/* BESTSELLERS — brand-kit HorizontalCarousel + ProductCarouselCard */}
        <BestsellersCarousel
          title="Bestsellers across every state"
          description="Top-rated refrigerators and freezers, updated regularly."
        />

        {/* Energy Rates Comparison */}
        {states.length > 0 && <section style={{ borderBottom: '1px solid #f3f4f6' }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>Electricity rates by state and DC</h2>
              <span className="meta">top 5 highest vs lowest · cents per kWh</span>
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#0469f0]/10 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-[#0469f0]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Highest Electricity Rates</h3>
                    <p className="text-xs text-gray-500">States where Energy Star efficiency matters most</p>
                  </div>
                </div>
                {highestRateStates.map((state, i) => (
                  <Link key={state.code} href={`/local/${STATE_SLUGS[state.code]}`}>
                    <div className="flex justify-between items-center py-3 border-b last:border-b-0 hover:bg-gray-50 px-2 -mx-2 rounded">
                      <div className="flex items-center gap-3">
                        <span className="text-gray-500 w-5 text-sm">{i + 1}.</span>
                        <Image
                          src={`/state-flags/fridge.com-${state.code.toLowerCase()}-flag.png`}
                          alt={`Flag of ${state.name} - Fridge.com`}
                          width={44}
                          height={28}
                          className="rounded border border-gray-200 shadow-sm"
                        />
                        <span className="font-medium text-sm text-gray-900">{state.name}</span>
                      </div>
                      <span className="text-[#0469f0] font-semibold text-sm">{state.rate}¢/kWh</span>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#ff9951]/10 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-[#ff9951]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Lowest Electricity Rates</h3>
                    <p className="text-xs text-gray-500">States with the most affordable refrigerator operation</p>
                  </div>
                </div>
                {lowestRateStates.map((state, i) => (
                  <Link key={state.code} href={`/local/${STATE_SLUGS[state.code]}`}>
                    <div className="flex justify-between items-center py-3 border-b last:border-b-0 hover:bg-gray-50 px-2 -mx-2 rounded">
                      <div className="flex items-center gap-3">
                        <span className="text-gray-500 w-5 text-sm">{i + 1}.</span>
                        <Image
                          src={`/state-flags/fridge.com-${state.code.toLowerCase()}-flag.png`}
                          alt={`Flag of ${state.name} - Fridge.com`}
                          width={44}
                          height={28}
                          className="rounded border border-gray-200 shadow-sm"
                        />
                        <span className="font-medium text-sm text-gray-900">{state.name}</span>
                      </div>
                      <span className="text-[#ff9951] font-semibold text-sm">{state.rate}¢/kWh</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>}

        {/* BROWSE BY STATE — V5 cards (design-system LocationStatCard) */}
        <section style={{ borderBottom: '1px solid #f3f4f6' }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>Browse by state</h2>
              <span className="meta">{data ? `${states.length} state and DC entries · ordered alphabetically` : 'Electricity data unavailable'}</span>
            </div>
            {!data && <p className="text-gray-600">Electricity rates and coverage counts are unavailable. Use the ZIP lookup to check your location.</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {states.map((state) => {
                const flagUrl = `/state-flags/fridge.com-${state.code.toLowerCase()}-flag.png`
                return (
                  <div key={state.code}>
                    <LocationStatCard
                    href={`/local/${STATE_SLUGS[state.code]}`}
                    name={state.name}
                    flagUrl={flagUrl}
                    flagAlt={`Flag of ${state.name}`}
                    headerEyebrow="Avg rate"
                    headerValue={state.rate.toFixed(1)}
                    headerValueSuffix="¢/kWh"
                    footerLabel={`View ${state.name} rates`}
                  />
                    <p className="text-xs text-gray-600 mt-2 px-1">{state.sourceCredit}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Featured Collections */}
        <section style={{ borderBottom: '1px solid #f3f4f6', background: 'var(--gray-50)' }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>Shop by your needs</h2>
              <span className="meta">climate-aware · rate-aware</span>
            </div>
            <p style={{ fontSize: 15, color: 'var(--gray-600)', lineHeight: 1.55, margin: '0 0 24px', maxWidth: 620 }}>
              Based on your climate and electricity rates, these collections help you find the right refrigerator.
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {FEATURED_COLLECTIONS.map((collection) => {
                const IconComponent = collection.icon
                return (
                  <Link key={collection.slug} href={`/collections/${collection.slug}`}>
                    <div className="group bg-white rounded-xl p-4 border border-gray-200 hover:border-[#ff9951] hover:shadow-md transition-all h-full flex flex-col">
                      <div className="w-10 h-10 rounded-lg bg-[#ff9951]/10 flex items-center justify-center mb-3">
                        <IconComponent className="h-5 w-5 text-[#ff9951]" />
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm mb-1">{collection.name}</h3>
                      <p className="text-xs text-gray-500 mb-3 flex-grow">{collection.desc}</p>
                      <span className="text-[#ff9951] text-sm font-medium flex items-center group-hover:gap-2 transition-all">
                        Browse Collection <ChevronRight className="h-4 w-4 ml-1" />
                      </span>
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>
        </section>

        {/* About FIS */}
        <section style={{ borderBottom: '1px solid #f3f4f6' }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 40 }}>
            <div className="ed-section-rule">
              <h2 style={{ fontSize: 26 }}>Fridge.com Intelligence Score (FIS)</h2>
              <span className="meta">data-driven · 0–100 · 4 components</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
              <p style={{ fontSize: 15, color: 'var(--gray-600)', lineHeight: 1.6, margin: 0, maxWidth: 620 }}>
                {FIS_CANONICAL_DEFINITION} {FIS_SCOPE_SUMMARY}
              </p>
              <Link href="/press" className="flex-shrink-0">
                <span className="inline-flex items-center gap-2 bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] font-semibold text-sm px-5 py-2.5 rounded-full transition-colors">
                  Read our research <ChevronRight className="h-4 w-4" />
                </span>
              </Link>
            </div>
            {/* The four components of a city or state score. Copy comes from
                lib/fis-factors.ts, read out of the scoring code — never retype
                it here (FRIDGE-381). */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {FIS_LOCATION_FACTORS.map((factor, i) => {
                const Icon = FIS_FACTOR_ICONS[i]
                const accent = i % 2 === 0 ? '#0469f0' : '#ff9951'
                return (
                  <div key={factor.name} className="bg-white rounded-xl p-5 border border-gray-200">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                      style={{ backgroundColor: `${accent}1a`, color: accent }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-1">{factor.name}</h3>
                    <p className="text-xs text-gray-500">{factor.description}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* FAQ — brand-kit FAQAccordion */}
        <section style={{ borderBottom: '1px solid #f3f4f6', background: 'var(--gray-50)' }}>
          <div className="ed-container" style={{ paddingTop: 48, paddingBottom: 48 }}>
            <FAQAccordion
              title="Frequently asked questions"
              columns={1}
              defaultOpen={[0]}
              bordered={false}
              items={faqs}
            />
          </div>
        </section>

        {/* Share Buttons - Bottom */}
        <section className="pt-8 pb-8">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <LocalShareButtons locationName="Local Fridge Finder" variant="bottom" />
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  )
}
